import {
  Ellipsis,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import InfoBox, { infoBoxVariants } from "components/InfoBox";
import { getOrdinalLabel } from "constants/proposals";
import { modalTypes } from "constants/types";
import { arrayOf, func, number, oneOfType, shape, string } from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import { computeGateInterception } from "utils/gateInterception";
import isNullishOrBlank from "utils/isNullishOrBlank";

import GateQuestionGroup from "./GateQuestionGroup";
import GateTransitionNotice from "./GateTransitionNotice";
import classes from "./KanbanGateMoveModal.module.scss";

const flattenQuestions = (crossedGates) =>
  crossedGates.flatMap((gate) =>
    gate.questions.map((question) => ({
      ...question,
      gateColumnId: gate.columnId,
    }))
  );

const KanbanGateMoveModal = ({
  entityId,
  prevColumnId,
  nextColumnId,
  fromTitle,
  toTitle,
  gateMode,
  crossedGates,
  targetIndex,
  moveThunk,
  fetchThunk,
  selectColumns,
  selectGateMode,
  onMoved,
}) => {
  const dispatch = useDispatch();
  const store = useStore();

  const [activeGates, setActiveGates] = useState(crossedGates);
  const [answers, setAnswers] = useState({});
  const [skipped, setSkipped] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [staleAlert, setStaleAlert] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const questions = useMemo(() => flattenQuestions(activeGates), [activeGates]);

  const invalidQuestionIds = useMemo(
    () =>
      questions
        .filter((question) => isNullishOrBlank(answers[question.id]))
        .map((question) => question.id),
    [questions, answers]
  );

  const gatesWithQuestions = useMemo(
    () => activeGates.filter((gate) => gate.questions.length > 0),
    [activeGates]
  );
  const showGroupHeaders = gatesWithQuestions.length > 1;

  const visibleInvalidQuestionIds = useMemo(
    () => (showErrors && !skipped ? new Set(invalidQuestionIds) : new Set()),
    [showErrors, skipped, invalidQuestionIds]
  );

  const setAnswer = (questionId, value) =>
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

  const setSkip = (value) => {
    setSkipped(value);
    setAnswers({});
    setShowErrors(false);
  };

  const close = () => dispatch(hideModal(modalTypes.KANBAN_GATE_MOVE_MODAL));

  const buildGatePayload = () =>
    skipped
      ? { skipped: true, note: note.trim() || null, answers: [] }
      : {
          skipped: false,
          note: null,
          answers: questions.map((question) => ({
            questionId: question.id,
            answer: String(answers[question.id]).trim(),
          })),
        };

  const handleConfirm = async () => {
    if (!skipped && invalidQuestionIds.length > 0) {
      setShowErrors(true);
      return;
    }

    setSubmitting(true);
    setStaleAlert(false);
    try {
      await dispatch(
        moveThunk({
          entityId,
          columnId: nextColumnId,
          position: targetIndex ?? null,
          gate: buildGatePayload(),
        })
      ).unwrap();
      onMoved?.();
      close();
    } catch (error) {
      if (error?.response?.status === 409) {
        await dispatch(fetchThunk());
        const fresh = computeGateInterception({
          columns: selectColumns(store.getState()),
          gateMode: selectGateMode(store.getState()),
          prevColumnId,
          nextColumnId,
        });
        if (!fresh.needsPrompt) {
          close();
          return;
        }
        setActiveGates(fresh.crossedGates);
        setAnswers({});
        setSkipped(false);
        setNote("");
        setShowErrors(false);
        setStaleAlert(true);
      } else {
        dispatch(showErrorModal(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      data-testid="kanban-gate-move-modal"
      className={clsx("fade-in", classes["kanban_gate_move_modal"])}
      show
      onDismiss={close}
      backdrop
      centered
    >
      <Modal.Header>
        <span>
          Move{" "}
          <span className={classes["kanban_gate_move_modal__title_stage"]}>
            {fromTitle}
          </span>
          {" → "}
          <span className={classes["kanban_gate_move_modal__title_stage"]}>
            {toTitle}
          </span>
        </span>
      </Modal.Header>

      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_500}
          className={classes["kanban_gate_move_modal__body"]}
        >
          <GateTransitionNotice
            fromTitle={fromTitle}
            toTitle={toTitle}
            gateMode={gateMode}
            skipped={skipped}
            onSkipChange={setSkip}
            note={note}
            onNoteChange={setNote}
          />

          {staleAlert && (
            <InfoBox variant={infoBoxVariants.warning}>
              The gate questions changed and were reloaded. Please answer them
              again.
            </InfoBox>
          )}

          <Flex
            direction={DIRECTION_CLASS.column}
            alignItems={ALIGN_CLASS.stretch}
            gap={GAP_CLASS.GAP_600}
            className={clsx(classes["kanban_gate_move_modal__questions"], {
              [classes["kanban_gate_move_modal__questions--dimmed"]]: skipped,
            })}
          >
            {gatesWithQuestions.map((gate, index) => (
              <GateQuestionGroup
                key={gate.columnId}
                gate={gate}
                ordinalLabel={getOrdinalLabel(index)}
                showHeaders={showGroupHeaders}
                disabled={skipped}
                answers={answers}
                onAnswerChange={setAnswer}
                invalidQuestionIds={visibleInvalidQuestionIds}
              />
            ))}
          </Flex>

          {showErrors && !skipped && invalidQuestionIds.length > 0 && (
            <InfoBox variant={infoBoxVariants.danger}>
              Since you have elected not to skip questions, you must answer the
              above.
            </InfoBox>
          )}
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <NewButton
          variant={NewButtonVariant.l2}
          onClick={close}
          disabled={submitting}
        >
          Cancel
        </NewButton>
        <NewButton
          variant={NewButtonVariant.l1}
          onClick={handleConfirm}
          disabled={submitting}
          className={classes["kanban_gate_move_modal__footer_button"]}
        >
          <Ellipsis
            text={`Move to ${(toTitle ?? "").toUpperCase()}`}
            classes={classes["kanban_gate_move_modal__footer_button__label"]}
          />
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

KanbanGateMoveModal.propTypes = {
  entityId: oneOfType([number, string]).isRequired,
  prevColumnId: oneOfType([number, string]).isRequired,
  nextColumnId: oneOfType([number, string]).isRequired,
  fromTitle: string,
  toTitle: string,
  gateMode: string.isRequired,
  crossedGates: arrayOf(
    shape({
      columnId: oneOfType([number, string]),
      title: string,
      questions: arrayOf(
        shape({
          id: number,
          type: string,
          text: string,
          options: arrayOf(string),
        })
      ),
    })
  ).isRequired,
  targetIndex: number,
  moveThunk: func.isRequired,
  fetchThunk: func.isRequired,
  selectColumns: func.isRequired,
  selectGateMode: func.isRequired,
  onMoved: func,
};

export default KanbanGateMoveModal;
